import updateBoxId from '@Services/updateBoxId';
import {
  Box,
  Grid,
  Typography,
  alpha,
  Autocomplete,
  InputLabel,
  FormControl,
  OutlinedInput,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/system';
import { ValidationResult } from 'joi';
import React, { useEffect, useState } from 'react';
import SbButton from '../../../../components/Button';
import Divider from '../../../../components/Divider';
import SbTextField from '../../../../components/Textfield';
import SBDropdown from '../../../../components/Dropdown';
import { ZoomAdjustedPopper } from '../../../../theme/zoomPopper';
import CreateShipmentContext from '../../../../context/CreateShipment';
import {
  AddProductType,
  BoxType,
  ProductErrorType,
  ProductType,
  createShipmentContextType,
} from '../../../../types/shipment';
import {
  defaultBoxState,
  defaultProductErrorState,
  defaultProductState,
  EU27,
  PRODUCT_ID_TYPE_OPTIONS,
} from '../../constants';
import BoxDimensions from '../BoxDimensions';
import ProductTable from '../ProductTable';
import { productDescriptionSchema } from './schema';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { SHIPMENT_TYPES } from '../../../GetRatesV2/Constant';
import { getChargeableWeight } from '@Reducers/GetRates/actions';
import { AddressType } from '@Reducers/GetRates/Type';
import { setMaximumCharegableWeight } from '@Reducers/GetRates';
import { isInTransit } from '../../../../utils/getPreviousBoxDimensions';
import { isActionAllowed } from '../../../../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import { useLocation } from 'react-router-dom';
import API from '@Services/apiAxios';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';

const Container = styled(Box)(({ theme }) => ({
  padding: '20px 12px',
  borderRadius: '8px',
  background: alpha(theme.palette.primary.light, 1),
}));

const Wrapper = styled(Box)(({ theme }) => ({
  padding: '20px',
  border: '1px solid var(--mui-palette-grey-300)',
  borderRadius: '8px',
  background: alpha(theme.palette.background.white, 1),
}));

interface ProductDescriptionProps {
  writeAccess?: boolean;
}

function getAddressObj({ pincode, city, country, state }: AddressType) {
  return { city, state, country, pincode };
}

const ProductDescription: React.FC<ProductDescriptionProps> = ({ writeAccess }): JSX.Element => {
  const {
    selectedBox,
    setSelectedBox,
    boxes,
    setBoxes,
    selectedProduct,
    setSelectedProduct,
    shipmentType,
    consignee,
    shipper,
    setChargeableWeight,
    pickupType: { selectedWearhouseAddress },
    setIsProductFormInvalid,
    setIsHSNValidating,
    getHSNCodeFetched,
    setGetHSNCodeFetched,
  } = React.useContext(CreateShipmentContext) as createShipmentContextType;
  const [productDetails, setProductDetails] = useState<AddProductType>({ ...defaultProductState });
  const [errors, setErrors] = useState<ProductErrorType>({ ...defaultProductErrorState });
  const [isSameAsProductId, setIsSameAsProductId] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const rates = useSelector((state: RootStateType) => state.getRates);
  const { shipmentById } = useSelector((state: RootStateType) => state.adminShipment);

  const { id, description, quantity, unitValue, HSNName, HSNCode } = productDetails;
  const writePermission = writeAccess ?? true;
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '', _country } = organizationData || {};
  const { pathname, search } = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);
  const { mutlipleShipmentsData } = useSelector((state: RootStateType) => state.trackShipment) || [];
  const checkForIsInTransit = isInTransit(mutlipleShipmentsData?.[0]);
  const isInTransitEditAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Shipment_Details.edit_package_after_in_transit,
    PERFORM_ACTION.write,
  );
  const isDisabled = shipmentById?.isInvoiceGenerated || (checkForIsInTransit && !isInTransitEditAllowed);

  const [isLoadingHSN, setIsLoadingHSN] = useState(false);

  const orgCountryName = _country?.name?.trim() || '';

  const getCountryName = (field: any): string => {
    if (!field) return '';
    if (typeof field === 'string') return field.trim();
    if (typeof field === 'object') {
      if (typeof field.country === 'string') return field.country.trim();
      if (field.country && typeof field.country === 'object' && typeof field.country.name === 'string') {
        return field.country.name.trim();
      }
      if (typeof field.name === 'string') return field.name.trim();
    }
    return '';
  };

  const shipperCountry = getCountryName(shipper);
  const consigneeCountry = getCountryName(consignee);

  let checkCountries: string[] = [];
  if (shipperCountry || consigneeCountry) {
    checkCountries = [shipperCountry, consigneeCountry].filter(Boolean);
  } else {
    checkCountries = [getCountryName(shipmentById?._shipperId), getCountryName(shipmentById?._consigneeId)].filter(
      Boolean,
    );
  }

  const destCountry = consigneeCountry || getCountryName(shipmentById?._consigneeId) || '';
  const isEUConsignee = EU27.some(c => c.toLowerCase() === destCountry.trim().toLowerCase());

  const isShipmentExport = orgCountryName
    ? checkCountries.some(c => c.toLowerCase() !== orgCountryName.toLowerCase())
    : false;

  const isGetHSNDisabled =
    !writePermission ||
    isDisabled ||
    !isEditMode ||
    isLoadingHSN ||
    getHSNCodeFetched ||
    (!productDetails.description?.trim() && (!selectedBox?.products || selectedBox.products.length === 0));

  const handleGetHSNCode = async () => {
    const destCountry = getCountryName(shipmentById?._consigneeId) || getCountryName(consignee) || '';

    const items: Array<{
      description: string;
      productId: number;
      isCurrentForm?: boolean;
      boxProductId?: string;
    }> = [];

    // 1. Add other products from the box (excluding the one being edited, to avoid duplicates)
    if (selectedBox?.products) {
      selectedBox.products.forEach(p => {
        if (productDetails.isUpdate && p._id === productDetails._id) {
          return;
        }
        if (p.description?.trim()) {
          items.push({
            description: p.description.trim(),
            productId: Math.floor(Math.random() * 1000000),
            boxProductId: p._id || p.id,
          });
        }
      });
    }

    // 2. Add the current form product
    if (productDetails.description?.trim()) {
      items.push({
        description: productDetails.description.trim(),
        productId: Math.floor(Math.random() * 1000000),
        isCurrentForm: true,
      });
    }

    if (items.length === 0) return;

    try {
      setIsLoadingHSN(true);
      const payload = {
        countryName: destCountry,
        items: items.map(item => ({
          description: item.description,
          productId: item.productId,
        })),
      };

      const response = await API.post('/speedboxbot/hsn', payload);

      if (response.data && !response.data.isError && (response.data.results || response.data.data)) {
        const results = response.data.results || response.data.data || [];
        setGetHSNCodeFetched(true);

        // Helper to find matching result for an item
        const getMatchForProduct = (item: { description: string; productId: number }) => {
          return results.find(
            (r: any) =>
              (r.productId !== undefined && String(r.productId) === String(item.productId)) ||
              (r.description && r.description.toLowerCase().trim() === item.description.toLowerCase().trim()) ||
              (r.suppliedDescription &&
                r.suppliedDescription.toLowerCase().trim() === item.description.toLowerCase().trim()),
          );
        };

        // Helper to map response to classifications
        const mapClassifications = (matchResult: any) => {
          if (!matchResult) return undefined;
          if (Array.isArray(matchResult.hsnCodes)) {
            return matchResult.hsnCodes.map((code: any) => ({
              hsnCode: code.HSNCode || code.hsnCode || '',
              hurricaneDescription: code.HSNName || code.hurricaneDescription || code.hsnName || '',
              confidence: code.confidence,
            }));
          }
          if (Array.isArray(matchResult.classifications)) {
            return matchResult.classifications;
          }
          return undefined;
        };

        // Update the form productDetails if it matches current form item
        const currentFormItem = items.find(i => i.isCurrentForm);
        if (currentFormItem) {
          const match = getMatchForProduct(currentFormItem);
          const classifications = mapClassifications(match);
          if (classifications && classifications.length > 0) {
            setProductDetails(prev => ({
              ...prev,
              classifications,
            }));
          }
        }

        // Update the products in the box
        if (selectedBox?.products?.length) {
          let boxProductsChanged = false;
          const updatedBoxProducts = selectedBox.products.map(p => {
            const correspondingItem = items.find(
              i =>
                (p._id && i.boxProductId === p._id) ||
                (p.id && i.boxProductId === p.id) ||
                (i.isCurrentForm && productDetails.isUpdate && p._id === productDetails._id) ||
                i.description.toLowerCase().trim() === p.description?.toLowerCase().trim(),
            );
            if (correspondingItem) {
              const match = getMatchForProduct(correspondingItem);
              const classifications = mapClassifications(match);
              if (classifications && classifications.length > 0) {
                boxProductsChanged = true;
                return {
                  ...p,
                  classifications,
                };
              }
            }
            return p;
          });

          if (boxProductsChanged) {
            const updatedSelectedBox = {
              ...selectedBox,
              products: updatedBoxProducts,
            };
            setSelectedBox(updatedSelectedBox);

            const updatedBoxes = boxes.map(box => (box.id === selectedBox.id ? updatedSelectedBox : box));
            setBoxes(updatedBoxes);
          }
        }
      } else {
        dispatch(
          SuccessErrorModalOpen({
            type: 'error',
            title: 'Oops!',
            subTitle: response.data?.msg || response.data?.message || 'Something went wrong.',
          }),
        );
      }
    } catch (error: any) {
      console.error('Error fetching HSN Code:', error);
      const errorMsg =
        error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Something went wrong.';
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: 'Oops!',
          subTitle: errorMsg,
        }),
      );
    } finally {
      setIsLoadingHSN(false);
    }
  };

  useEffect(() => {
    if (selectedProduct) {
      setProductDetails({ ...defaultProductState, ...selectedProduct });
    } else {
      setProductDetails({ ...defaultProductState });
    }
    setIsSameAsProductId(false);
  }, [selectedProduct]);

  useEffect(() => {
    const isCreateSingle = pathname === '/shipment/create-single' || pathname === '/shipment/create-shipment';
    const hasReference = search.includes('ref=') && search.includes('accountId=');

    if (isCreateSingle || hasReference) {
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }
  }, [pathname, search]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    validateProperty(event);
    const name = event.target.name as keyof AddProductType;
    const { value } = event.target;
    const productDataTemp = { ...productDetails };
    if (name === 'description' || name === 'HSNCode' || name === 'HSNName') {
      productDataTemp[name] = value;
      if (name === 'description') {
        productDataTemp.classifications = undefined;
        setGetHSNCodeFetched(false);
      }
    } else if (name === 'quantity') {
      productDataTemp[name] = parseInt(value);
    } else if (name === 'unitValue') {
      productDataTemp[name] = value;
    } else if (name === 'productId') {
      productDataTemp.productId = value;
      if (isSameAsProductId) {
        productDataTemp.merchantProductId = value;
      }
    } else if (name === 'merchantProductId') {
      productDataTemp.merchantProductId = value;
    } else if (name === 'standardManufacturerProductId') {
      productDataTemp.standardManufacturerProductId = value;
    } else if (name === 'nonStandardManufacturerProductId') {
      productDataTemp.nonStandardManufacturerProductId = value;
    } else if (name === 'productIdType') {
      productDataTemp.productIdType = value;
    }
    setProductDetails({ ...productDataTemp });
  }

  const handleSameAsProductIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsSameAsProductId(checked);
    if (checked) {
      setProductDetails(prev => ({
        ...prev,
        merchantProductId: prev.productId || '',
      }));
    }
  };

  function finalCharegableWTByID() {
    if (shipmentById?.shipmentNumber) {
      const obj = {
        destination: { ...getAddressObj(shipmentById?._consigneeId) },
        // source: { ...getAddressObj(selectedWearhouseAddress ? selectedWearhouseAddress : shipper) },
        source: {
          ...getAddressObj(
            shipmentById?._packageId?.isDropOff ? shipmentById?._warehouseAddressId : shipmentById?._shipperId,
          ),
        },
        package: {
          box: shipmentById?._packageId?.box?.map(box => ({
            weight: box.weight.toString(),
            length: Number(box.length),
            width: Number(box.width),
            height: Number(box.height),
          })),
          unitOfMeasurement: shipmentType.unit,
        },
        document: shipmentType.type === SHIPMENT_TYPES[1].value,
        _courierId: shipmentById?.sellingPricing?._courierId,
        shipmentEdit: true,
        inbound: shipmentById?.sellingPricing?.inbound,
        outbound: shipmentById?.sellingPricing?.outbound,
        shipmentNumber: shipmentById?.shipmentNumber,
      };
      // console.log(obj);
      dispatch(getChargeableWeight({ ...obj }));
    }
  }

  function updateFinalChargableWeight(organizationName?: boolean) {
    if (shipmentById?.shipmentNumber) {
      const obj = {
        destination: { ...getAddressObj(shipmentById?._consigneeId) },
        // source: { ...getAddressObj(selectedWearhouseAddress ? selectedWearhouseAddress : shipper) },
        source: { ...getAddressObj(shipmentById?._shipperId) },
        package: {
          box: boxes.map(box => ({
            weight: box.weight.toString(),
            length: Number(box.length),
            width: Number(box.width),
            height: Number(box.height),
          })),
          unitOfMeasurement: shipmentType.unit,
        },
        document: shipmentType.type === SHIPMENT_TYPES[1].value,
        _courierId: shipmentById?.sellingPricing?._courierId,
        shipmentEdit: true,
        shipmentNumber: shipmentById?.shipmentNumber,
        inbound: shipmentById?.sellingPricing?.inbound,
        outbound: shipmentById?.sellingPricing?.outbound,
      };
      dispatch(getChargeableWeight({ ...obj }));
    } else {
      const obj = {
        destination: { ...getAddressObj(consignee) },
        source: {
          ...getAddressObj(selectedWearhouseAddress?.label ? selectedWearhouseAddress : shipper),
        },
        package: {
          box: boxes.map(box => ({
            weight: box.weight.toString(),
            length: Number(box.length),
            width: Number(box.width),
            height: Number(box.height),
          })),
          unitOfMeasurement: shipmentType.unit,
        },
        document: shipmentType.type === SHIPMENT_TYPES[1].value,
      };
      dispatch(getChargeableWeight({ ...obj, organizationName }));
    }

    const updateChargeableWeight = rates?.chareableWeight?.maxChargeableWt;

    if (updateChargeableWeight) {
      setChargeableWeight(updateChargeableWeight);
    }
  }

  useEffect(() => {
    if (shipmentById?.buyingPricing) {
      finalCharegableWTByID();
      // setChargeableWeight(shipmentById?.sellingPricing?.chargeableWeight);
      if (shipmentById.sellingPricing?.chargeableWeight)
        dispatch(setMaximumCharegableWeight(shipmentById?.sellingPricing?.chargeableWeight));
    }
  }, [shipmentById?.shipmentNumber, shipmentById?._packageId?.box, shipmentType.unit]);

  function addProduct() {
    const boxesTemp = structuredClone(boxes);
    const productsTemp = structuredClone(selectedBox.products);
    const boxIndex = boxesTemp.findIndex(box => box.id === selectedBox.id);
    const productInd = id ? productsTemp.findIndex(product => product.id === id) : -1;

    // set product index
    const _id = productInd > -1 ? productInd.toString() : boxesTemp[boxIndex].products.length.toString();
    console.log({ boxIndex, productInd, _id });

    // productsTemp[productInd] = { ...productDetails };
    if (boxesTemp[boxIndex].products.length && productInd > -1) {
      // update selected index
      boxesTemp[boxIndex].products[productInd] = {
        ...productDetails,
        id: _id,
        unitValue:
          typeof productDetails.unitValue === 'string' ? Number(productDetails.unitValue) : productDetails.unitValue,
      };
    } else {
      // add new product
      boxesTemp[boxIndex].products = [...boxesTemp[boxIndex].products, { ...productDetails, id: _id }].map(x => ({
        ...x,
        unitValue: typeof x.unitValue === 'string' ? Number(x.unitValue) : x.unitValue,
      }));
    }

    setBoxes([...boxesTemp]);
    setSelectedBox({
      ...selectedBox,
      products: [...boxesTemp[boxIndex].products],
    });
    // setSelectedProduct(null);
    // setSelectedProduct({ ...defaultProductState });
    setSelectedProduct({ ...defaultProductState, id: `${boxesTemp[boxIndex].products.length}` });
    setIsSameAsProductId(false);

    updateFinalChargableWeight();
  }

  function updateProduct(): void {
    // Ensure selectedBox and products are defined
    if (!selectedBox || !selectedBox.products) {
      return;
    }

    // Cast unitValue to number if it's a string
    const updatedProductDetails = {
      ...productDetails,
      unitValue:
        typeof productDetails.unitValue === 'string' ? Number(productDetails.unitValue) : productDetails.unitValue,
    };

    // Find the index of the item with matching id in the products array
    const indexToUpdate = selectedBox.products.findIndex(item => item._id === updatedProductDetails._id);

    // If a matching item is found, update it with the data
    if (indexToUpdate !== -1) {
      // Create a new array with the updated item
      const updatedProducts = [
        ...selectedBox.products.slice(0, indexToUpdate), // Items before the updated item
        updatedProductDetails, // Updated item
        ...selectedBox.products.slice(indexToUpdate + 1), // Items after the updated item
      ];

      // Assign the updated array back to selectedBox
      selectedBox.products = updatedProducts;

      const updateBoxes = boxes?.map(box => (box.id === selectedBox.id ? selectedBox : box));
      setBoxes(updateBoxes);

      setSelectedBox({
        ...selectedBox,
        products: updatedProducts,
      });
      setSelectedProduct({ ...defaultProductState });
      setIsSameAsProductId(false);
      // setSelectedProduct({ ...selectedProduct });
    } else {
      // If no matching item is found, simply push the data to the products array
      // selectedBox.products.push(updatedProductDetails);

      setSelectedBox({
        ...selectedBox,
        products: selectedBox.products,
      });
      setIsSameAsProductId(false);
      // setSelectedProduct({ ...defaultProductState });
    }
    updateFinalChargableWeight(name === 'Cargo Solutions Network');
  }

  function cancelProduct() {
    const boxesTemp = Array.from(boxes);
    const boxIndex = boxesTemp.findIndex(box => box.id === selectedBox.id);
    setBoxes([...boxesTemp]);
    setSelectedBox({
      ...selectedBox,
      products: [...boxesTemp[boxIndex].products],
    });
    setSelectedProduct(null);
    setIsSameAsProductId(false);
  }

  function validateProductData() {
    const _productDetails = { ...productDetails };
    delete _productDetails.isUpdate;
    const result: ValidationResult = productDescriptionSchema.validate(_productDetails, {
      abortEarly: false,
      stripUnknown: true,
    });
    const { error } = result;
    const errorData: ProductErrorType = {};

    if (error) {
      for (const item of error.details) {
        const name = item.path[0];
        const message = item.message;
        errorData[name as keyof ProductErrorType] = message;
      }
    }

    if (isShipmentExport && !productDetails.HSNCode?.trim()) {
      errorData.HSNCode = 'Please enter HSN Code';
    }

    if (Object.keys(errorData).length > 0) {
      setErrors(errorData);
      return;
    }

    if (productDetails.isUpdate) {
      updateProduct();
    } else {
      addProduct();
    }
  }

  // Allow standard keyboard characters + line breaks
  const allowedRegex = new RegExp('^[a-zA-Z0-9 .,!?@#$%^&*()_\\-+=[\\]{}:;"\'<>/|\\\\~`\\n\\r]*$');

  const validateProperty = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const errorData = { ...errors };

    // 🔹 Special character validation only for text fields
    if (
      name === 'description' ||
      name === 'HSNName' ||
      name === 'HSNCode' ||
      name === 'productId' ||
      name === 'merchantProductId' ||
      name === 'standardManufacturerProductId' ||
      name === 'nonStandardManufacturerProductId'
    ) {
      if (!allowedRegex.test(value)) {
        errorData[name as keyof ProductErrorType] = 'Only standard keyboard characters are allowed.';
        setErrors(errorData);
        return;
      }
    }

    // 🔹 Joi Schema Validation
    const obj = { [name]: value };
    const result = productDescriptionSchema.validate(obj);
    const { error } = result;

    const errorMessage = error && error.details[0].context?.key === name ? error.details[0].message : null;

    if (errorMessage && name in defaultProductErrorState) {
      errorData[name as keyof ProductErrorType] = errorMessage;
    } else if (name === 'HSNCode' && isShipmentExport && !value?.trim()) {
      errorData.HSNCode = 'Please enter HSN Code';
    } else {
      errorData[name as keyof ProductErrorType] = '';
    }

    setErrors(errorData);
  };

  function calculateTotalValue() {
    if (quantity && unitValue)
      setProductDetails({
        ...productDetails,
        totalValue: quantity * (typeof unitValue === 'string' ? Number(unitValue) : unitValue),
      });
  }

  function handleEditProduct(data: ProductType) {
    setSelectedProduct({ ...data, isUpdate: true });
  }

  function handleDeleteProduct(ind: number) {
    let productsTemp = structuredClone(selectedBox.products);
    productsTemp.splice(ind, 1);
    const boxesTemp = structuredClone(boxes);
    const boxInd = boxesTemp.findIndex(box => box.id === selectedBox.id);
    productsTemp = productsTemp.map((p, ind) => ({ ...p, id: ind.toString() }));
    boxesTemp[boxInd].products = productsTemp;
    setSelectedBox({ ...selectedBox, products: productsTemp });

    const arr = updateBoxId<BoxType>(boxesTemp, defaultBoxState, 0);
    setBoxes([...arr]);
  }
  useEffect(() => {
    const isShipperValid = shipper.city && shipper.country && shipper.pincode;
    const isConsigneeValid = consignee.city && consignee.country && consignee.pincode;
    if (boxes.length >= 1 && isShipperValid && isConsigneeValid) {
      updateFinalChargableWeight(name === 'Cargo Solutions Network');
    }
  }, [boxes.length]);

  const hasErrors = Object.values(errors).some(error => error);

  const isProductInvalid =
    hasErrors ||
    !productDetails.description ||
    !productDetails.quantity ||
    !productDetails.unitValue ||
    (isShipmentExport && !productDetails.HSNCode?.trim());

  const isFormDirtyOrUpdate =
    productDetails.isUpdate ||
    !!(
      productDetails.description ||
      productDetails.quantity ||
      productDetails.unitValue ||
      productDetails.HSNCode ||
      productDetails.productId ||
      productDetails.merchantProductId ||
      productDetails.standardManufacturerProductId ||
      productDetails.nonStandardManufacturerProductId
    );

  const isFormInvalid = isFormDirtyOrUpdate && isProductInvalid;

  useEffect(() => {
    if (setIsProductFormInvalid) {
      setIsProductFormInvalid(isFormInvalid);
    }
  }, [isFormInvalid, setIsProductFormInvalid]);

  useEffect(() => {
    if (setIsHSNValidating) {
      setIsHSNValidating(isLoadingHSN);
    }
  }, [isLoadingHSN, setIsHSNValidating]);

  return (
    <Container>
      <Wrapper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
          <Typography className="md-text mb-1" fontWeight={'600'}>
            Box Dimensions
          </Typography>
          {!(
            pathname === '/shipment/create-single' ||
            pathname === '/shipment/create-shipment' ||
            (search.includes('ref=') && search.includes('accountId='))
          ) && (
            <SbButton
              sx={{
                borderRadius: '4px !important',
                zIndex: 1,
                '@media (max-width: 600px)': {
                  marginTop: '10px',
                  marginBottom: '10px',
                },
              }}
              variant="contained"
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  setIsEditMode(true);
                }
              }}
              disabled={isDisabled}
            >
              Edit
            </SbButton>
          )}
        </Box>
        <BoxDimensions writePermission={writePermission} isDisabled={isDisabled || !isEditMode} />
        <Divider className="my-4" />
        <Box className="px-2 py-0 rounded">
          <Typography className="md-text mb-2" fontWeight={'600'}>
            Add product
          </Typography>
          <Grid container columnSpacing={2} rowSpacing={1}>
            <Grid item md={12} xs={12}>
              <Grid container columnSpacing={2} rowSpacing={1}>
                <Grid item md={4} xs={12}>
                  <Grid container columnSpacing={2} rowSpacing={1}>
                    <Grid item md={12} xs={12}>
                      <SbTextField
                        required
                        multiline
                        rows={5}
                        placeholder="Enter Description"
                        label="Description"
                        onChange={handleChange}
                        name="description"
                        value={description}
                        error={errors.description}
                        style={{ height: '100%' }}
                        disabled={!writePermission || isDisabled || !isEditMode}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item md={8} xs={12}>
                  <Grid container columnSpacing={2} rowSpacing={1.5}>
                    <Grid item md={6} xs={12}>
                      <SbTextField
                        placeholder="Enter Quantity"
                        label="Quantity"
                        type="integer"
                        onChange={handleChange}
                        name="quantity"
                        value={quantity ? JSON.stringify(quantity) : ''}
                        onBlur={calculateTotalValue}
                        required
                        error={errors.quantity}
                        disabled={!writePermission || isDisabled || !isEditMode}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <SbTextField
                        placeholder={`Unit value in ${shipmentType.currency}`}
                        label="Unit Value"
                        onChange={handleChange}
                        name="unitValue"
                        value={unitValue ? unitValue : ''}
                        onBlur={calculateTotalValue}
                        type="number"
                        required
                        error={errors.unitValue}
                        disabled={!writePermission || isDisabled || !isEditMode}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <SbTextField
                        placeholder="Enter HSN Name"
                        label="HSN Name"
                        onChange={handleChange}
                        name="HSNName"
                        value={HSNName}
                        error={errors.HSNName}
                        disabled={!writePermission || isDisabled || !isEditMode}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {productDetails.classifications && productDetails.classifications.length > 0 ? (
                        <Autocomplete
                          freeSolo
                          openOnFocus
                          PopperComponent={ZoomAdjustedPopper}
                          options={productDetails.classifications}
                          getOptionLabel={option => {
                            if (typeof option === 'string') return option;
                            return option.hsnCode || '';
                          }}
                          value={HSNCode || ''}
                          onChange={(_event: any, newValue: any) => {
                            let code = '';
                            if (typeof newValue === 'string') {
                              code = newValue;
                            } else if (newValue && newValue.hsnCode) {
                              code = newValue.hsnCode;
                            }
                            setProductDetails(prev => ({
                              ...prev,
                              HSNCode: code,
                            }));
                            setErrors(prev => ({
                              ...prev,
                              HSNCode: '',
                            }));
                          }}
                          onInputChange={(_event: any, newInputValue: string) => {
                            setProductDetails(prev => ({
                              ...prev,
                              HSNCode: newInputValue,
                            }));
                            const dummyEvent = {
                              target: {
                                name: 'HSNCode',
                                value: newInputValue,
                              },
                            } as React.ChangeEvent<HTMLInputElement>;
                            validateProperty(dummyEvent);
                          }}
                          disabled={!writePermission || isDisabled || !isEditMode}
                          ListboxProps={{
                            style: {
                              padding: 0,
                            },
                          }}
                          renderOption={(props, option) => {
                            const { key, ...optionProps } = props as any;
                            return (
                              <Box
                                component="li"
                                key={key || option.hsnCode + option.hurricaneDescription}
                                {...optionProps}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  width: '100%',
                                  padding: '10px 16px !important',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  '&:hover': {
                                    backgroundColor: 'action.hover',
                                  },
                                  '&.Mui-selected, &[aria-selected="true"]': {
                                    backgroundColor: 'action.selected',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                      backgroundColor: 'action.focus',
                                    },
                                  },
                                  '&:last-child': {
                                    borderBottom: 'none',
                                  },
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.925rem',
                                    color: 'text.primary',
                                    lineHeight: 1.3,
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {option.hurricaneDescription?.toUpperCase() || option.hsnCode?.toUpperCase()}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '0.8rem',
                                    marginTop: '4px',
                                    fontWeight: 400,
                                  }}
                                >
                                  HSN Code: {option.hsnCode}
                                </Typography>
                              </Box>
                            );
                          }}
                          renderInput={params => {
                            const { InputProps, inputProps, ...restParams } = params;
                            const hasError = !!errors.HSNCode;
                            return (
                              <div className="sb-textfield-wrapper">
                                <InputLabel
                                  className="sb-textfield-label"
                                  required={isShipmentExport}
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    marginBottom: '8px',
                                    display: 'block',
                                    letterSpacing: '0.3px',
                                    color: 'text.primary',
                                  }}
                                >
                                  HSN Code
                                </InputLabel>
                                <FormControl className="sb-textfield-control" fullWidth error={hasError}>
                                  <OutlinedInput
                                    {...restParams}
                                    {...InputProps}
                                    inputProps={{
                                      ...inputProps,
                                      placeholder: 'Enter HSN Code...',
                                    }}
                                    error={hasError}
                                    className="sb-textfield-input"
                                    sx={{
                                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                      backdropFilter: 'blur(10px)',
                                      padding: '4px',
                                      borderRadius: '12px',
                                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                      },
                                      border: hasError ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                                      '& .MuiOutlinedInput-input': {
                                        fontSize: '0.95rem',
                                        fontWeight: 400,
                                        padding: '12px 16px',
                                        letterSpacing: '0.3px',
                                        height: 'auto',
                                      },
                                      '&.Mui-focused': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.85)',
                                        backdropFilter: 'blur(12px)',
                                        border: hasError ? '1px solid #d32f2f' : '1px solid #1976d2',
                                        boxShadow: hasError
                                          ? '0 0 0 2px rgba(211, 47, 47, 0.12)'
                                          : '0 0 0 2px rgba(25, 118, 210, 0.12)',
                                      },
                                      '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        backdropFilter: 'blur(12px)',
                                        borderColor: hasError ? '#d32f2f' : '#1976d2',
                                      },
                                      '&.Mui-disabled': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                        cursor: 'not-allowed',
                                        '& .MuiOutlinedInput-input': {
                                          color: 'text.disabled',
                                          cursor: 'not-allowed',
                                        },
                                      },
                                    }}
                                    disabled={!writePermission || isDisabled || !isEditMode}
                                  />
                                  {hasError && (
                                    <FormHelperText
                                      error
                                      sx={{
                                        margin: '6px 4px 0 4px',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        color: '#d32f2f',
                                        letterSpacing: '0.2px',
                                      }}
                                    >
                                      {errors.HSNCode}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </div>
                            );
                          }}
                        />
                      ) : (
                        <SbTextField
                          required={isShipmentExport}
                          placeholder="Enter HSN Code"
                          label="HSN Code"
                          onChange={handleChange}
                          name="HSNCode"
                          value={HSNCode}
                          error={errors.HSNCode}
                          disabled={!writePermission || isDisabled || !isEditMode}
                        />
                      )}
                      {isShipmentExport && !getHSNCodeFetched && (
                        <SbButton
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1, height: '36px', fontSize: '14px' }}
                          onClick={handleGetHSNCode}
                          disabled={isGetHSNDisabled}
                          loading={isLoadingHSN}
                        >
                          Get HSN Code
                        </SbButton>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mt: 1.5, mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.8px',
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                        }}
                      >
                        Product Identification {isEUConsignee && '& EU Compliance'}
                      </Typography>
                      <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
                    </Box>

                    <Grid container columnSpacing={2} rowSpacing={1.5}>
                      <Grid item md={6} xs={12}>
                        <SbTextField
                          placeholder="Enter Product ID"
                          label="Product ID"
                          onChange={handleChange}
                          name="productId"
                          value={productDetails.productId || ''}
                          error={errors.productId}
                          disabled={!writePermission || isDisabled || !isEditMode}
                        />
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <SBDropdown
                          id="productIdType"
                          label="Product ID Type"
                          name="productIdType"
                          placeholder="Select Product ID Type"
                          options={PRODUCT_ID_TYPE_OPTIONS}
                          value={productDetails.productIdType ? [productDetails.productIdType] : ['SKU']}
                          onChange={selected => {
                            const val = selected[0] || 'SKU';
                            setProductDetails(prev => ({
                              ...prev,
                              productIdType: val,
                            }));
                          }}
                          disabled={!writePermission || isDisabled || !isEditMode}
                        />
                      </Grid>
                      {isEUConsignee && (
                        <>
                          <Grid item md={6} xs={12}>
                            <Box sx={{ width: '100%' }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: '8px',
                                  minHeight: '21px',
                                }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    letterSpacing: '0.3px',
                                    color: 'text.primary',
                                  }}
                                >
                                  Merchant Product ID
                                </Typography>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={isSameAsProductId}
                                      onChange={handleSameAsProductIdChange}
                                      disabled={!writePermission || isDisabled || !isEditMode}
                                      sx={{
                                        p: '2px',
                                        mr: 0.5,
                                        '&.Mui-checked': { color: 'primary.main' },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '0.78rem',
                                        color: isSameAsProductId ? 'primary.main' : 'text.secondary',
                                        fontWeight: isSameAsProductId ? 600 : 400,
                                        userSelect: 'none',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      Same as Product ID
                                    </Typography>
                                  }
                                  sx={{ m: 0 }}
                                />
                              </Box>
                              <SbTextField
                                placeholder={isSameAsProductId ? 'Synced with Product ID' : 'Enter Merchant Product ID'}
                                onChange={handleChange}
                                name="merchantProductId"
                                value={productDetails.merchantProductId || ''}
                                error={errors.merchantProductId}
                                disabled={isSameAsProductId || !writePermission || isDisabled || !isEditMode}
                              />
                            </Box>
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <SbTextField
                              placeholder="Enter Standard Manufacturer Product ID"
                              label="Standard Manufacturer Product ID"
                              onChange={handleChange}
                              name="standardManufacturerProductId"
                              value={productDetails.standardManufacturerProductId || ''}
                              error={errors.standardManufacturerProductId}
                              disabled={!writePermission || isDisabled || !isEditMode}
                            />
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <SbTextField
                              placeholder="Enter Non-Standard Manufacturer Product ID"
                              label="Non-Standard Manufacturer Product ID"
                              onChange={handleChange}
                              name="nonStandardManufacturerProductId"
                              value={productDetails.nonStandardManufacturerProductId || ''}
                              error={errors.nonStandardManufacturerProductId}
                              disabled={!writePermission || isDisabled || !isEditMode}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                </Grid>
                <Grid container item xs={12} columnSpacing={1} justifyContent={'end'} className="mt-1">
                  {productDetails.isUpdate && (
                    <Grid item>
                      <SbButton fullWidth variant="outlined" onClick={cancelProduct}>
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                  <Grid item>
                    <SbButton
                      fullWidth
                      variant="contained"
                      onClick={validateProductData}
                      disabled={!writePermission || isDisabled || !isEditMode || isProductInvalid}
                    >
                      {productDetails.isUpdate ? 'Update' : 'Add'}
                    </SbButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        {selectedBox?.products?.length ? (
          <>
            <Divider className="my-4" />
            <ProductTable
              title={'Product Description'}
              enableActions
              products={selectedBox.products}
              handleEdit={handleEditProduct}
              handleDelete={handleDeleteProduct}
              selectedProduct={selectedProduct}
            />
          </>
        ) : (
          <></>
        )}
      </Wrapper>
    </Container>
  );
};

export default ProductDescription;
